/**
 * Filtro do download em massa de MTR/CDF, composto no front.
 *
 * O mtr-manager só conhece `mtr_manifest`: janela, unidades e materiais. O recorte da tela
 * (status, UF, planejadas ocultas) mora no agreements. Então o portal lista as coletas no
 * agreements com os filtros da tela, manda os ids ao mtr-manager e, passado o teto de ids, cai
 * para janela/unidades/materiais (um pouco mais largo do que a tela mostra).
 */

import { addDays } from 'date-fns';
import { fetchCollects, type CollectsFilterInput } from '@/integrations/agreements/client';
import type { EstablishmentOption, MaterialOption } from '@/integrations/generatorApi/client';
import {
  MTR_BULK_COLLECT_ID_LIMIT,
  type CdfBulkDownloadSummary,
  type EstablishmentNames,
  type MtrBulkDownloadFilterInput,
  type MtrBulkDownloadSummary,
  type MtrBulkPreviewItem,
} from '@/integrations/mtrManager/client';
import { EXCLUDE_PLANNED_MATERIAL_IDS, buildCollectsFilter, type MtrBulkScope } from '@/hooks/useCollectsApiData';
import type { FilterState } from '@/hooks/useFilters';
import { startOfToday } from '@/utils/coletasCalendar';

/** Página máxima da listagem do agreements. */
const LIST_PAGE_SIZE = 500;
const CONCURRENT_PAGES = 3;

export interface MtrBulkFilterParams {
  filters: FilterState | undefined;
  profile: { is_internal?: boolean; generator_id?: number | null } | null;
  generators: Array<{ id: string; external_id: string }>;
  establishments: EstablishmentOption[];
  scope: MtrBulkScope;
}

export interface ResolvedMtrBulkFilter {
  filter: MtrBulkDownloadFilterInput;
  establishmentNames: EstablishmentNames;
  /** Coletas que a tela lista com esses filtros (count do agreements). */
  totalCollects: number;
  /** A listagem passou do teto de ids: o lote foi só por janela/unidades/materiais. */
  idsCapped: boolean;
}

/**
 * O mesmo filtro da listagem para o escopo do lote:
 * - `historico`: coletas realizadas (exclui `planned`), na janela da tela;
 * - `proximas`: planejadas de hoje a +365 dias, sem as planejadas ocultas (RCC e Óleo).
 */
export function listFilterForScope(params: Omit<MtrBulkFilterParams, 'establishments'> & { establishments: Array<{ id: string; external_id: string }> }): CollectsFilterInput {
  const today = startOfToday();
  const options =
    params.scope === 'proximas'
      ? {
          status: ['planned'],
          excludePlannedMaterialIds: EXCLUDE_PLANNED_MATERIAL_IDS,
          window: { start: today, end: addDays(today, 365) },
        }
      : {
          excludeStatus: ['planned'],
          excludePlannedMaterialIds: EXCLUDE_PLANNED_MATERIAL_IDS,
        };

  const { has_mtr, ...filter } = buildCollectsFilter(
    params.filters,
    params.profile,
    params.generators,
    params.establishments,
    1,
    LIST_PAGE_SIZE,
    options,
  );
  void has_mtr;
  return filter;
}

/**
 * Unidades do lote (`generator_id` do mtr-manager = id de estabelecimento): as selecionadas; senão
 * as dos geradores selecionados; senão as da UF selecionada. Sem filtro, o escopo é o do token.
 */
export function establishmentIdsForBulk(
  listFilter: CollectsFilterInput,
  filters: FilterState | undefined,
  establishments: EstablishmentOption[],
): number[] | undefined {
  if (listFilter.establishment_id?.length) return listFilter.establishment_id;

  if (listFilter.generator_id?.length) {
    const roots = new Set(listFilter.generator_id.map(String));
    const ids = establishments
      .filter((e) => e.generator_id != null && roots.has(e.generator_id))
      .map((e) => Number(e.id))
      .filter((id) => !Number.isNaN(id));
    return ids.length ? ids : undefined;
  }

  if (filters?.states?.length) {
    const states = new Set(filters.states);
    const ids = establishments
      .filter((e) => e.state && states.has(e.state))
      .map((e) => Number(e.id))
      .filter((id) => !Number.isNaN(id));
    // Nenhuma unidade na UF: nada deve sair. O id 0 não existe, então o lote vem vazio.
    return ids.length ? ids : [0];
  }

  return undefined;
}

export async function resolveMtrBulkFilter(params: MtrBulkFilterParams): Promise<ResolvedMtrBulkFilter> {
  const listFilter = listFilterForScope(params);
  const first = await fetchCollects(listFilter);
  const totalCollects = first.pagination?.count ?? first.data.length;
  const idsCapped = totalCollects > MTR_BULK_COLLECT_ID_LIMIT;

  let collectIds: string[] | undefined;
  if (!idsCapped) {
    collectIds = first.data.map((item) => item.id);
    const lastPage = first.pagination?.last_page ?? 1;
    const remaining: number[] = [];
    for (let page = 2; page <= lastPage; page += 1) remaining.push(page);
    for (let i = 0; i < remaining.length; i += CONCURRENT_PAGES) {
      const results = await Promise.all(
        remaining.slice(i, i + CONCURRENT_PAGES).map((page) => fetchCollects({ ...listFilter, page })),
      );
      results.forEach((result) => collectIds!.push(...result.data.map((item) => item.id)));
    }
  }

  return {
    filter: {
      date_from: listFilter.date_from ?? '',
      date_to: listFilter.date_to ?? '',
      generator_id: establishmentIdsForBulk(listFilter, params.filters, params.establishments),
      material_id: listFilter.material_id ?? undefined,
      collect_id: collectIds,
    },
    establishmentNames: Object.fromEntries(params.establishments.map((e) => [e.id, e.name])),
    totalCollects,
    idsCapped,
  };
}

/** Resumo de MTR como o modal lê: nomes de unidade e material rotulados no front, mais o total de coletas da tela. */
export type MtrBulkDownloadSummaryView = Omit<MtrBulkDownloadSummary, 'preview'> & {
  total_collects: number;
  preview: Array<MtrBulkPreviewItem & { establishment_name: string | null; material_name: string | null }>;
};

export type CdfBulkDownloadSummaryView = CdfBulkDownloadSummary & { total_collects: number };

export function enrichMtrSummary(
  summary: MtrBulkDownloadSummary,
  establishments: Array<{ id: string; name: string }>,
  materials: MaterialOption[],
  totalCollects: number,
): MtrBulkDownloadSummaryView {
  const establishmentById = new Map(establishments.map((e) => [e.id, e.name]));
  const materialById = new Map(materials.map((m) => [m.id, m.name]));
  return {
    ...summary,
    total_collects: totalCollects,
    preview: summary.preview.map((item) => ({
      ...item,
      // Sem cadastro para o id, o próprio id: degrada o rótulo, não a linha.
      establishment_name:
        item.generator_id != null ? (establishmentById.get(String(item.generator_id)) ?? String(item.generator_id)) : null,
      material_name: item.material_id != null ? (materialById.get(String(item.material_id)) ?? String(item.material_id)) : null,
    })),
  };
}

export function withTotalCollects(summary: CdfBulkDownloadSummary, totalCollects: number): CdfBulkDownloadSummaryView {
  return { ...summary, total_collects: totalCollects };
}
